import { readContract, sendTransaction, signMessage, waitForTransactionReceipt, writeContract } from '@wagmi/core';
import { config } from '../../config/wagmi';
import { EnsWallet } from '../../application/ens/ports';
import { TokenGateway } from '../../application/governance/ports';
import { WZEUS_ADDRESS, ZEUS_ADDRESS } from '../../domain/governance/wrap';

/** The connected wallet, through wagmi, for the ENS and wrap use cases. */

type Hex = `0x${string}`;

export const wagmiEnsWallet = (address: string | null): EnsWallet => ({
  address,
  signMessage: (message) => signMessage(config, { message }),
  async pay(to, wei) {
    const hash = await sendTransaction(config, { to: to as Hex, value: wei });
    await waitForTransactionReceipt(config, { hash });
    return hash;
  },
});

const ERC20_ABI = [
  { name: 'decimals', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
  { name: 'balanceOf', type: 'function', stateMutability: 'view', inputs: [{ name: 'account', type: 'address' }], outputs: [{ type: 'uint256' }] },
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
] as const;

const WZEUS_ABI = [
  ...ERC20_ABI,
  {
    name: 'depositFor',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'value', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
  { name: 'getVotes', type: 'function', stateMutability: 'view', inputs: [{ name: 'account', type: 'address' }], outputs: [{ type: 'uint256' }] },
  { name: 'delegates', type: 'function', stateMutability: 'view', inputs: [{ name: 'account', type: 'address' }], outputs: [{ type: 'address' }] },
  { name: 'delegate', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'delegatee', type: 'address' }], outputs: [] },
  {
    name: 'withdrawTo',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'value', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
] as const;

export const wagmiTokenGateway = (account: string): TokenGateway => {
  const me = account as Hex;
  const done = async (hash: Hex) => {
    await waitForTransactionReceipt(config, { hash });
  };
  return {
    async read() {
      const [decimals, zeus, wzeus, allowance, votes, delegate] = await Promise.all([
        readContract(config, { address: ZEUS_ADDRESS, abi: ERC20_ABI, functionName: 'decimals' }),
        readContract(config, { address: ZEUS_ADDRESS, abi: ERC20_ABI, functionName: 'balanceOf', args: [me] }),
        readContract(config, { address: WZEUS_ADDRESS, abi: WZEUS_ABI, functionName: 'balanceOf', args: [me] }),
        readContract(config, { address: ZEUS_ADDRESS, abi: ERC20_ABI, functionName: 'allowance', args: [me, WZEUS_ADDRESS] }),
        readContract(config, { address: WZEUS_ADDRESS, abi: WZEUS_ABI, functionName: 'getVotes', args: [me] }),
        readContract(config, { address: WZEUS_ADDRESS, abi: WZEUS_ABI, functionName: 'delegates', args: [me] }),
      ]);
      const none = /^0x0{40}$/i.test(delegate);
      return { decimals: Number(decimals), zeus, wzeus, allowance, votes, delegate: none ? null : delegate };
    },
    approve: async (amount) => done(await writeContract(config, { address: ZEUS_ADDRESS, abi: ERC20_ABI, functionName: 'approve', args: [WZEUS_ADDRESS, amount] })),
    wrap: async (amount) => done(await writeContract(config, { address: WZEUS_ADDRESS, abi: WZEUS_ABI, functionName: 'depositFor', args: [me, amount] })),
    unwrap: async (amount) => done(await writeContract(config, { address: WZEUS_ADDRESS, abi: WZEUS_ABI, functionName: 'withdrawTo', args: [me, amount] })),
    delegateToSelf: async () => done(await writeContract(config, { address: WZEUS_ADDRESS, abi: WZEUS_ABI, functionName: 'delegate', args: [me] })),
  };
};
